import { useEffect, useState } from "react";

import { useGetCheckoutConfirmation } from "../api/generated/endpoints/billing/billing";
import { CheckoutReturn, type CheckoutReturnProps } from "../features/onboarding/checkout-return";
import { recordConfirmedPurchase } from "../platform/onboarding-tracking";
import { useFirstAdPurchase, type PurchaseRecorder } from "../platform/onboarding-tracking/use-purchase";

export type FirstAdPurchaseProps = CheckoutReturnProps & { sessionId: string | null };
export const PURCHASE_PROOF_POLL_MS = 2_000;
export const PURCHASE_PROOF_WAIT_MS = 60_000;

/** Explicit recorder injection keeps the actual query-to-event consumer testable with fake sinks. */
export function createFirstAdPurchase(record: PurchaseRecorder) {
  return function FirstAdPurchaseView({ sessionId, ...props }: FirstAdPurchaseProps) {
    const [expired, setExpired] = useState(false);
    const enabled = props.result === "success" && sessionId !== null && sessionId.length <= 255 && /^cs_[A-Za-z0-9_]+$/.test(sessionId);
    useEffect(() => {
      setExpired(false);
      if (!enabled) return;
      const timer = setTimeout(() => setExpired(true), PURCHASE_PROOF_WAIT_MS);
      return () => clearTimeout(timer);
    }, [enabled, props.teamId, sessionId]);
    const query = useGetCheckoutConfirmation(props.teamId, { session_id: sessionId ?? "", first_ad_brand_id: props.brandId, first_ad_template_id: props.templateId }, { query: {
      enabled, staleTime: 0, refetchOnMount: "always", retry: false,
      refetchOnWindowFocus: false, refetchOnReconnect: false,
      refetchInterval: (current) => !expired && current.state.status !== "error"
        && !current.state.data?.purchase ? PURCHASE_PROOF_POLL_MS : false,
    } });
    const confirmed = enabled && query.isSuccess && query.isFetchedAfterMount && !query.isFetching ? query.data.purchase : null;
    // Stripe records lowercase ISO currency; Meta's transport contract uses uppercase.
    const proof = confirmed ? { ...confirmed, currency: confirmed.currency.toUpperCase() } : null;
    useFirstAdPurchase(proof, record);
    return <CheckoutReturn {...props} />;
  };
}

/** Success/session query params are only lookup context. Only the server's invoice proof is money evidence. */
export const FirstAdPurchase = createFirstAdPurchase(recordConfirmedPurchase);
